import { useState, type PropsWithChildren, type ReactNode } from 'react';
import styled, { useTheme, type DefaultTheme } from 'styled-components';

import { box, HStack, type BoxProps } from '../../Core';
import { SectionHeader } from '../../Forms/styles';
import { Icon, IconName } from '../../Icons';
import { FormSection } from '../styles';

const CollapsibleFormSectionWrapper = styled(FormSection)<
  {
    isExpanded: boolean;
    theme: DefaultTheme;
  } & Omit<BoxProps, 'flex'>
>`
  ${box}; // Apply all box props
  max-height: ${({ isExpanded }) => (isExpanded ? 600 : 50)}px;
  overflow: hidden;
  transition: max-height 0.15s ease-in-out;

  ${SectionHeader} {
    margin-bottom: ${
      ({ isExpanded, theme }) => (isExpanded ? theme.spacing8 : -1) /* Prevents a visual stutter on closing */
    }px;
  }
`;

type ManagedCollapsibleFormSectionProps = PropsWithChildren<{
  header: ReactNode;
  isExpanded: boolean;
  onToggle: (isExpanded: boolean) => void;
}> &
  Omit<BoxProps, 'flex'>;

export function ManagedCollapsibleFormSection({
  children,
  header,
  isExpanded,
  onToggle,
  ...rest
}: ManagedCollapsibleFormSectionProps) {
  const theme = useTheme();

  return (
    <CollapsibleFormSectionWrapper isExpanded={isExpanded} {...rest}>
      <SectionHeader data-testid="section-header" onClick={() => onToggle(!isExpanded)}>
        <HStack gap="spacing8" justifyContent="space-between">
          {header}
          <Icon icon={IconName.ChevronDown} rotate={isExpanded ? 0 : 90} size={theme.baseSize} />
        </HStack>
      </SectionHeader>
      {isExpanded && children}
    </CollapsibleFormSectionWrapper>
  );
}

type UnmanagedCollapsibleFormSectionProps = PropsWithChildren<{
  header: ManagedCollapsibleFormSectionProps['header'];
  initiallyExpanded?: boolean;
}> &
  Omit<BoxProps, 'flex'>;

export function UnmanagedCollapsibleFormSection({
  children,
  header,
  initiallyExpanded = false,
  ...rest
}: UnmanagedCollapsibleFormSectionProps) {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  return (
    <ManagedCollapsibleFormSection {...rest} header={header} isExpanded={isExpanded} onToggle={setIsExpanded}>
      {children}
    </ManagedCollapsibleFormSection>
  );
}
